import { useMapDataTimeline as mapDataTimeline } from "@pages/Dashboard/hooks/useMapDataTimeline";
import { applyFiltersToEvents } from "@pages/Dashboard/utils/applyFilters";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useFiltersDrawerContext } from "./FiltersDrawer.context";
import { useFilterContext } from "./Filter.context";
import GanttDataContext from "./GanttData.context";

const GanttDataProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const {
    drawerFilters = {
      status: [],
      channels: [],
    },
  } = useFiltersDrawerContext();
  const { view } = useFilterContext();

  const [mapGroupD1, setMapGroupD1] = useState<Sentinel.Timeline.GroupedEvent[]>([]);
  const [mapGroupD2, setMapGroupD2] = useState<Sentinel.Timeline.GroupedEvent[]>([]);
  const [rawData, setRawData] = useState<Sentinel.Timeline.New | undefined>(undefined);

  const applyFiltersToGroup = useCallback((group: Sentinel.Timeline.GroupedEvent) => {
    const filteredEvents = applyFiltersToEvents(
      group.events,
      drawerFilters.status,
      drawerFilters.channels
    );
    return { ...group, events: filteredEvents };
  }, [drawerFilters.status, drawerFilters.channels]);

  const processData = useCallback((data: Sentinel.Timeline.New | undefined) => {
    if (!data) {
      setMapGroupD1([]);
      setMapGroupD2([]);
      return;
    }

    const { mapGroupD1: mappedD1, mapGroupD2: mappedD2 } = mapDataTimeline(data, view);

    const filteredD1 = mappedD1
      .map(applyFiltersToGroup)
      .filter((group) => group.events?.length > 0);

    const filteredD2 = mappedD2
      .map(applyFiltersToGroup)
      .filter((group) => group.events?.length > 0);

    setMapGroupD1(filteredD1);
    setMapGroupD2(filteredD2);
  }, [view, applyFiltersToGroup]);

  const updateData = useCallback((data: Sentinel.Timeline.New) => {
    setRawData(data);
  }, []);

  const refreshMap = useCallback(() => {
    if (rawData) {
      processData(rawData);
    }
  }, [rawData, processData]);

  useEffect(() => {
    processData(rawData);
  }, [rawData, processData]);

  const contextValue = useMemo(
    () => ({
      mapGroupD1,
      mapGroupD2,
      rawData,
      updateData,
      refreshMap,
    }),
    [mapGroupD1, mapGroupD2, rawData, updateData, refreshMap]
  );

  return (
    <GanttDataContext.Provider value={contextValue}>
      {children}
    </GanttDataContext.Provider>
  );
};

export default GanttDataProvider;
